import React from 'react';

// Per-variant content for the three AI Forge Super Flow landing pages, keyed by the partner's
// `key` in the DB. Each variant is a separate Partner record because `skipInvestorLead` is a field
// on that record: triage and cohort are Section B only (true), declined runs Section A then B
// (false). Three records also give three unique referralCodes, so the ~1,500 triage applicants and
// ~90 cohort members stay distinguishable in reporting.
//
// Copy and accents are held as JSX because the headings mix an accent span, hand-set line breaks
// and (on cohort) an inline logo — cheaper to read here than to encode into a mini-format.
//
// Reason: every path is written out in full. Parcel resolves require() at build time only when the
// argument is a static string — interpolating the variant/name leaves a runtime require(), which
// throws "require is not defined" in the browser.
const TRIAGE_PHOTO = require('../../../../images/super-flow/ai-forge/triage-applicants/top-banner-one.png');
const TRIAGE_LOCKUP = require('../../../../images/super-flow/ai-forge/triage-applicants/top-banner-two.png');
const TRIAGE_BENEFIT = require('../../../../images/super-flow/ai-forge/triage-applicants/top-banner-three.png');
const TRIAGE_AI_GIRL = require('../../../../images/super-flow/ai-forge/triage-applicants/ai-girl.png');

const COHORT_PHOTO = require('../../../../images/super-flow/ai-forge/cohort-applicants/top-banner-one.png');
const COHORT_LOCKUP = require('../../../../images/super-flow/ai-forge/cohort-applicants/top-banner-two.png');
const COHORT_BENEFIT = require('../../../../images/super-flow/ai-forge/cohort-applicants/top-banner-three.png');
const COHORT_CARD_ONE = require('../../../../images/super-flow/ai-forge/cohort-applicants/card-one.png');
const COHORT_CARD_TWO = require('../../../../images/super-flow/ai-forge/cohort-applicants/card-two.png');
const COHORT_CARD_THREE = require('../../../../images/super-flow/ai-forge/cohort-applicants/card-three.png');

const DECLINED_LOCKUP = require('../../../../images/super-flow/ai-forge/declined-applicants/top-banner-one.png');
const DECLINED_ROBOT = require('../../../../images/super-flow/ai-forge/declined-applicants/top-banner-two.png');
const DECLINED_PROMO = require('../../../../images/super-flow/ai-forge/declined-applicants/top-banner-three.png');
const DECLINED_THUMB = require('../../../../images/super-flow/ai-forge/declined-applicants/video-thumbnail.png');

export const AI_FORGE_LOGO = require('../../../../images/super-flow/ai-forge/ai-forge-logo.png');
// The full Diligentsia lockup — the page header uses it, and the cohort headline sets it inline.
export const DILIGENTSIA_LOGO = require('../../../../images/logo.png');
export const INFO_ICON = require('../../../../images/super-flow/ai-forge/cohort-applicants/info-icon.png');
export const KOALENDAR = 'https://koalendar.com/e/meet-with-mark-bernstein';

// The platform line is shared verbatim by all three heroes.
const PLATFORM_LINE = <>Diligentsia is the UK's leading automated diligence platform —<br /> <span className="c15">trusted by VCs, advisors and founders like you</span></>;

export const AI_FORGE_VARIANTS = {
    // ---- All triage applicants (~1,500/yr) — Section B only ----
    AFTSF: {
        section: 'oneform',
        eyebrow: 'Recommended for all AI Forge Applicants',
        title: <>Let's Help You Speed Up<br />Our <span className="af-accent">Acceptance Decision</span></>,
        sub: PLATFORM_LINE,
        primary: { label: "FREE - Let's Go!" },
        secondary: { label: 'Book A Demo', href: KOALENDAR },
        footnote: 'No credit card  ·  Takes 30 minutes  ·  Free',
        art: [TRIAGE_PHOTO, TRIAGE_LOCKUP, TRIAGE_BENEFIT],
        oneform: {
            badge: 'FREE · 30 min',
            heading: <>Complete Investment<br />One<span className="af-pink">Form</span> Once</>,
            image: TRIAGE_AI_GIRL,
            bullets: [
                'Speeds up your investment preparation',
                'Answer 10 sections of questions bespoked to your business',
                'Report gives you risks and recommendations on what to improve',
                'Whatever the result - you will get a set of additional valuable reports',
                'Can show to us and later to investors if you choose',
            ],
        },
    },

    // ---- All successful cohort applicants (~90/yr) — Section B only ----
    AFCSF: {
        section: 'offer',
        eyebrow: 'Recommended For All AI Forge Cohort Members',
        title: <>Help Us Prepare You For And Get Funded More Quickly With <img className="af-title-logo" src={require('../../../../images/logo.png')} alt="Diligentsia" /></>,
        sub: PLATFORM_LINE,
        primary: { small: 'New user', label: 'Start For FREE' },
        secondary: { label: 'Login', to: '/login' },
        demoLink: 'Book a demo',
        art: [COHORT_PHOTO, COHORT_LOCKUP, COHORT_BENEFIT],
        offer: {
            heading: 'Offer For All Cohort Members',
            footnote: 'Upgrade* = Lite plans FREE for cohort members; “AIFORGE20” unlocks 20% discount on Pro – from £63.25 per month',
            cards: [
                {
                    step: 'STEP 01', title: 'OneForm Investment Readiness Module',
                    body: 'OnForm checklist. Get a full investment readiness report plus a bonus set of research reports — how others see you.',
                    image: COHORT_CARD_ONE,
                },
                {
                    step: 'STEP 02 - ON US!', title: 'LITE Diligence Pack',
                    body: 'Investors show more interest in pre-diligenced companies. Create once and use with all investors.',
                    image: COHORT_CARD_TWO,
                },
                {
                    step: 'STEP 03', title: 'InvestorMatch — 1-click connect',
                    body: 'Get matched with 40+ leading UK VCs in one click. Introductions to investors who fund companies like yours.',
                    image: COHORT_CARD_THREE,
                },
            ],
        },
    },

    // ---- Declined / rejected applicants (~1,410/yr) — Section A then Section B ----
    AFDSF: {
        section: 'video',
        eyebrow: 'Unsuccessful AI Forge Application? No Worries!',
        eyebrowTone: 'red',
        title: <>Let's Help You <span className="af-accent">Get Funded</span><br />Elsewhere</>,
        sub: <>Our partner, Diligentsia specialises in getting companies like yours quickly<br />investment ready, and then matching you with its database of investors</>,
        subSmall: PLATFORM_LINE,
        primary: { small: 'New user', label: 'Start For FREE' },
        secondary: { label: 'Login', to: '/login' },
        demoLink: 'Book a demo',
        art: [DECLINED_LOCKUP, DECLINED_ROBOT, DECLINED_PROMO],
        video: {
            heading: 'Can Diligentsia Help You With Your Raise?',
            // The film is not finished; the still carries the play affordance until a source lands.
            thumbnail: DECLINED_THUMB,
        },
    },
};

export const isAiForgeKey = (key) => Object.prototype.hasOwnProperty.call(AI_FORGE_VARIANTS, key);
